// 📂 frontend/src/components/SettingsPanel.tsx
import React, { useState } from 'react';
import { useToast } from '../hooks/useToast';
import emailApi from '../services/emailApi';

interface Settings {
  gmailEmail: string;
  gmailPassword: string;
  smtpHost: string;
  smtpPort: number;
  emailSignature: string;
  dailyLimit: number;
  timezone: string;
  autoBackup: boolean;
  darkMode: boolean;
  notifications: {
    emailSent: boolean;
    emailFailed: boolean;
    userLogin: boolean;
  };
}

interface SettingsPanelProps {
  settings: Settings;
  onSettingsUpdate: (settings: Settings) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: any; // ThemeConfig from ThemeContext
  currentTheme: string;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  settings,
  onSettingsUpdate,
  theme,
  currentTheme
}) => {
  const { toast } = useToast();
  const [localSettings, setLocalSettings] = useState(settings);
  const [activeTab, setActiveTab] = useState<'email' | 'general' | 'notifications' | 'backup' | 'password'>('email');
  const [showPassword, setShowPassword] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handleSave = () => {
    onSettingsUpdate(localSettings);
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    // Simulare test email
    await new Promise(resolve => setTimeout(resolve, 2000));
    setTestingEmail(false);
    toast.success('Test Email', 'Test email trimis cu succes!');
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error('Eroare', 'Completeaza toate campurile');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Eroare', 'Parolele noi nu coincid');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Eroare', 'Parola noua trebuie sa aiba minim 6 caractere');
      return;
    }
    setChangingPassword(true);
    try {
      const result = await emailApi.changePassword(oldPassword, newPassword);
      if ((result as Record<string, unknown>).success) {
        toast.success('Succes', 'Parola a fost schimbata cu succes!');
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error('Eroare', (result as Record<string, unknown>).error as string || 'Eroare la schimbarea parolei');
      }
    } catch (error) {
      toast.error('Eroare', error instanceof Error ? error.message : 'Eroare la schimbarea parolei');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleExportSettings = () => {
    const dataStr = JSON.stringify(localSettings, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `email-automation-settings-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleImportSettings = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedSettings = JSON.parse(e.target?.result as string);
        setLocalSettings(importedSettings);
        toast.success('Import', 'Setări importate cu succes!');
      } catch (error) {
        toast.error('Eroare', 'Eroare la importarea setărilor!');
      }
    };
    reader.readAsText(file);
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1000px'
    },
    header: {
      marginBottom: '30px'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      margin: '0 0 8px 0'
    },
    subtitle: {
      color: theme.colors.textSecondary,
      fontSize: '14px',
      margin: 0
    },
    tabs: {
      display: 'flex',
      gap: '10px',
      marginBottom: '30px',
      borderBottom: `2px solid ${theme.colors.border}`,
      paddingBottom: '10px'
    },
    tab: {
      padding: '10px 20px',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: `${theme.borderRadius.medium} ${theme.borderRadius.medium} 0 0`,
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      color: theme.colors.textSecondary,
      transition: theme.transitions.fast
    },
    tabActive: {
      backgroundColor: theme.colors.primary,
      color: 'white'
    },
    section: {
      backgroundColor: theme.colors.surface,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.large,
      padding: '30px',
      marginBottom: '20px'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500',
      fontSize: '14px',
      color: theme.colors.text
    },
    input: {
      width: '100%',
      padding: '10px',
      borderRadius: theme.borderRadius.medium,
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.background,
      color: theme.colors.text,
      fontSize: '14px'
    },
    textarea: {
      width: '100%',
      padding: '10px',
      borderRadius: theme.borderRadius.medium,
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.background,
      color: theme.colors.text,
      fontSize: '14px',
      resize: 'vertical' as const,
      minHeight: '100px',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      padding: '10px',
      borderRadius: theme.borderRadius.medium,
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.background,
      color: theme.colors.text,
      fontSize: '14px',
      cursor: 'pointer'
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      cursor: 'pointer'
    },
    button: {
      padding: '10px 20px',
      borderRadius: theme.borderRadius.medium,
      border: 'none',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: theme.transitions.fast
    },
    buttonPrimary: {
      backgroundColor: theme.colors.primary,
      color: 'white'
    },
    buttonSecondary: {
      backgroundColor: 'transparent',
      color: theme.colors.text,
      border: `1px solid ${theme.colors.border}`
    },
    helpText: {
      fontSize: '12px',
      color: theme.colors.textSecondary,
      marginTop: '4px'
    },
    alert: {
      padding: '16px',
      borderRadius: theme.borderRadius.medium,
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    alertWarning: {
      backgroundColor: theme.colors.warning + '22',
      border: `1px solid ${theme.colors.warning}44`,
      color: theme.colors.warning
    },
    alertInfo: {
      backgroundColor: theme.colors.primary + '22',
      border: `1px solid ${theme.colors.primary}44`,
      color: theme.colors.primary
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title} className={currentTheme === 'futuristic' ? 'effect-glow' : ''}>
          ⚙️ Setări
        </h1>
        <p style={styles.subtitle}>
          Configurează sistemul de email automation
        </p>
      </div>

      {/* Tabs */}
      <div className="settings-tabs" style={styles.tabs}>
        <button
          onClick={() => setActiveTab('email')}
          style={{ ...styles.tab, ...(activeTab === 'email' ? styles.tabActive : {}) }}
        >
          📧 Email & SMTP
        </button>
        <button
          onClick={() => setActiveTab('general')}
          style={{ ...styles.tab, ...(activeTab === 'general' ? styles.tabActive : {}) }}
        >
          🌐 General
        </button>
        <button
          onClick={() => setActiveTab('notifications')}
          style={{ ...styles.tab, ...(activeTab === 'notifications' ? styles.tabActive : {}) }}
        >
          🔔 Notificări
        </button>
        <button
          onClick={() => setActiveTab('backup')}
          style={{ ...styles.tab, ...(activeTab === 'backup' ? styles.tabActive : {}) }}
        >
          💾 Backup & Export
        </button>
        <button
          onClick={() => setActiveTab('password')}
          style={{ ...styles.tab, ...(activeTab === 'password' ? styles.tabActive : {}) }}
        >
          🔑 Parola
        </button>
      </div>

      {/* Email & SMTP Settings */}
      {activeTab === 'email' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span>📧</span> Configurare Email & SMTP
          </h2>

          <div style={{ ...styles.alert, ...styles.alertWarning }}>
            <span>⚠️</span>
            <div>
              <strong>Important:</strong> Pentru a folosi Gmail, activează "App Passwords" în setările contului Google.
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Email Gmail</label>
            <input
              type="email"
              value={localSettings.gmailEmail}
              onChange={(e) => setLocalSettings({ ...localSettings, gmailEmail: e.target.value })}
              placeholder="your-email@gmail.com"
              style={styles.input}
            />
            <p style={styles.helpText}>Adresa de email de la care se vor trimite mesajele</p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Parolă Aplicație Gmail</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={localSettings.gmailPassword}
                onChange={(e) => setLocalSettings({ ...localSettings, gmailPassword: e.target.value })}
                placeholder="xxxx-xxxx-xxxx-xxxx"
                style={styles.input}
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            <p style={styles.helpText}>
              <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary }}>
                Cum generez o parolă de aplicație?
              </a>
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div style={styles.formGroup}>
              <label style={styles.label}>SMTP Host</label>
              <input
                type="text"
                value={localSettings.smtpHost}
                onChange={(e) => setLocalSettings({ ...localSettings, smtpHost: e.target.value })}
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>SMTP Port</label>
              <input
                type="number"
                value={localSettings.smtpPort}
                onChange={(e) => setLocalSettings({ ...localSettings, smtpPort: parseInt(e.target.value) })}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Semnătură Email</label>
            <textarea
              value={localSettings.emailSignature}
              onChange={(e) => setLocalSettings({ ...localSettings, emailSignature: e.target.value })}
              placeholder="Cu respect,&#10;Echipa Email Automation"
              style={styles.textarea}
            />
            <p style={styles.helpText}>Această semnătură va fi adăugată automat la toate email-urile</p>
          </div>

          <button
            onClick={handleTestEmail}
            disabled={testingEmail || !localSettings.gmailEmail || !localSettings.gmailPassword}
            style={{
              ...styles.button,
              ...styles.buttonSecondary,
              opacity: (testingEmail || !localSettings.gmailEmail || !localSettings.gmailPassword) ? 0.6 : 1,
              cursor: (testingEmail || !localSettings.gmailEmail || !localSettings.gmailPassword) ? 'not-allowed' : 'pointer'
            }}
          >
            {testingEmail ? '⏳ Se trimite...' : '🧪 Test Conexiune Email'}
          </button>
        </div>
      )}

      {/* General Settings */}
      {activeTab === 'general' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span>🌐</span> Setări Generale
          </h2>

          <div style={styles.formGroup}>
            <label style={styles.label}>Limită Zilnică Email-uri</label>
            <input
              type="number"
              value={localSettings.dailyLimit}
              onChange={(e) => setLocalSettings({ ...localSettings, dailyLimit: parseInt(e.target.value) })}
              min="1"
              max="500"
              style={styles.input}
            />
            <p style={styles.helpText}>Numărul maxim de email-uri care pot fi trimise într-o zi (Gmail: max 500/zi)</p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Fus Orar</label>
            <select
              value={localSettings.timezone}
              onChange={(e) => setLocalSettings({ ...localSettings, timezone: e.target.value })}
              style={styles.select}
            >
              <option value="Europe/Bucharest">Europe/București (GMT+2)</option>
              <option value="Europe/London">Europe/London (GMT+0)</option>
              <option value="America/New_York">America/New York (GMT-5)</option>
              <option value="America/Los_Angeles">America/Los Angeles (GMT-8)</option>
              <option value="Asia/Tokyo">Asia/Tokyo (GMT+9)</option>
            </select>
            <p style={styles.helpText}>Fusul orar folosit pentru programarea email-urilor</p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.checkbox}>
              <input
                type="checkbox"
                checked={localSettings.autoBackup}
                onChange={(e) => setLocalSettings({ ...localSettings, autoBackup: e.target.checked })}
              />
              <span>Backup automat zilnic</span>
            </label>
            <p style={styles.helpText}>Salvează automat o copie de siguranță a datelor în fiecare zi</p>
          </div>

          <div style={{ ...styles.alert, ...styles.alertInfo }}>
            <span>ℹ️</span>
            <div>
              <strong>Notă:</strong> Modificările la setările generale vor fi aplicate la următoarea pornire a aplicației.
            </div>
          </div>
        </div>
      )}

      {/* Notifications Settings */}
      {activeTab === 'notifications' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span>🔔</span> Setări Notificări
          </h2>

          <div style={styles.formGroup}>
            <label style={styles.checkbox}>
              <input
                type="checkbox"
                checked={localSettings.notifications.emailSent}
                onChange={(e) => setLocalSettings({
                  ...localSettings,
                  notifications: { ...localSettings.notifications, emailSent: e.target.checked }
                })}
              />
              <span>Notificare când un email este trimis cu succes</span>
            </label>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.checkbox}>
              <input
                type="checkbox"
                checked={localSettings.notifications.emailFailed}
                onChange={(e) => setLocalSettings({
                  ...localSettings,
                  notifications: { ...localSettings.notifications, emailFailed: e.target.checked }
                })}
              />
              <span>Notificare când un email eșuează</span>
            </label>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.checkbox}>
              <input
                type="checkbox"
                checked={localSettings.notifications.userLogin}
                onChange={(e) => setLocalSettings({
                  ...localSettings,
                  notifications: { ...localSettings.notifications, userLogin: e.target.checked }
                })}
              />
              <span>Notificare când un utilizator se conectează</span>
            </label>
          </div>

          <div style={{ marginTop: '30px', padding: '20px', backgroundColor: theme.colors.background, borderRadius: theme.borderRadius.medium }}>
            <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Preview Notificare</h3>
            <div style={{
              padding: '12px',
              backgroundColor: theme.colors.surface,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.small,
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '20px' }}>✅</span>
              <div>
                <div style={{ fontWeight: '500' }}>Email Trimis cu Succes</div>
                <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                  Email-ul către client@example.com a fost trimis
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backup & Export Settings */}
      {activeTab === 'backup' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span>💾</span> Backup & Export
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
            <div style={{
              padding: '30px',
              backgroundColor: theme.colors.background,
              borderRadius: theme.borderRadius.large,
              textAlign: 'center' as const
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📤</div>
              <h3 style={{ marginBottom: '12px' }}>Export Setări</h3>
              <p style={{ fontSize: '14px', color: theme.colors.textSecondary, marginBottom: '20px' }}>
                Descarcă toate setările într-un fișier JSON
              </p>
              <button
                onClick={handleExportSettings}
                style={{ ...styles.button, ...styles.buttonPrimary }}
              >
                Export Setări
              </button>
            </div>

            <div style={{
              padding: '30px',
              backgroundColor: theme.colors.background,
              borderRadius: theme.borderRadius.large,
              textAlign: 'center' as const
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📥</div>
              <h3 style={{ marginBottom: '12px' }}>Import Setări</h3>
              <p style={{ fontSize: '14px', color: theme.colors.textSecondary, marginBottom: '20px' }}>
                Încarcă setări dintr-un fișier JSON
              </p>
              <label style={{
                ...styles.button,
                ...styles.buttonSecondary,
                display: 'inline-block'
              }}>
                Import Setări
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportSettings}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>

          <div style={{ ...styles.alert, ...styles.alertInfo }}>
            <span>💡</span>
            <div>
              <strong>Sfat:</strong> Fă backup regulat la setări și date importante. Poți programa backup-uri automate din setările generale.
            </div>
          </div>
        </div>
      )}

      {/* Password Change */}
      {activeTab === 'password' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span>🔑</span> Schimba Parola
          </h2>

          <div style={styles.formGroup}>
            <label style={styles.label}>Parola actuala</label>
            <input
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              placeholder="Introdu parola actuala"
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Parola noua</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Introdu parola noua"
              style={styles.input}
            />
            <p style={styles.helpText}>Minim 6 caractere</p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Confirma parola noua</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirma parola noua"
              style={styles.input}
            />
          </div>

          <button
            onClick={handleChangePassword}
            disabled={changingPassword || !oldPassword || !newPassword || !confirmPassword}
            style={{
              ...styles.button,
              ...styles.buttonPrimary,
              opacity: (changingPassword || !oldPassword || !newPassword || !confirmPassword) ? 0.6 : 1,
              cursor: (changingPassword || !oldPassword || !newPassword || !confirmPassword) ? 'not-allowed' : 'pointer'
            }}
          >
            {changingPassword ? '⏳ Se schimba...' : '🔑 Schimba Parola'}
          </button>
        </div>
      )}

      {/* Save Button */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '30px' }}>
        <button
          onClick={() => setLocalSettings(settings)}
          style={{ ...styles.button, ...styles.buttonSecondary }}
        >
          Anulează Modificările
        </button>
        <button
          onClick={handleSave}
          style={{ ...styles.button, ...styles.buttonPrimary }}
          className={currentTheme === 'futuristic' ? 'btn futuristic-btn' : ''}
        >
          💾 Salvează Setările
        </button>
      </div>
    </div>
  );
};

export default SettingsPanel;